import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { publicAxios } from "@/lib/axios";
import { AxiosError } from "axios";
import { campaignRecoveryDestination, rememberCampaignRecovery } from '@/lib/campaigns/campaignEntryRouting';

const ResetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const resetToken = searchParams.get("code");
  const [loginDestination] = useState(() => campaignRecoveryDestination(localStorage, searchParams.get('redirect'), Date.now()));

  useEffect(() => {
    if (!resetToken) {
      toast({
        title: t('resetPassword.toast.invalidToken'),
        description: t('resetPassword.toast.invalidLink'),
        variant: "destructive",
      });
      navigate("/forgot-password");
    }
  }, [resetToken, navigate, toast, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: t('resetPassword.toast.errorTitle'),
        description: t('resetPassword.toast.passwordMismatch'),
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: t('resetPassword.toast.errorTitle'),
        description: t('resetPassword.toast.passwordTooShort'),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      await publicAxios.post('/api/auth/reset-password', {
        code: resetToken,
        password: password,
        passwordConfirmation: password,
      });

      setIsSuccess(true);
      toast({
        title: t('resetPassword.toast.successTitle'),
        description: t('resetPassword.toast.successDescription'),
      });
    } catch (error) {
      const axiosError = error as AxiosError;
      toast({
        title: t('resetPassword.toast.errorTitle'),
        description: (axiosError.response?.data as any)?.error?.message || t('resetPassword.toast.errorDescription'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img
                src="/images/autoclipper-logo-full.png"
                alt="Autoclipper Logo"
                className="h-12 w-auto"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-white">{t('resetPassword.success.title')}</CardTitle>
            <CardDescription className="text-gray-400">
              {t('resetPassword.success.subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-300 mb-4">
              {t('resetPassword.success.message')}
            </p>
            <Button
              onClick={() => {
                rememberCampaignRecovery(localStorage, null, Date.now());
                navigate(loginDestination);
              }}
              className="w-full bg-gradient-to-r from-lime-400 to-cyan-400 hover:from-lime-500 hover:to-cyan-500 text-black font-semibold rounded-full shadow-lg"
            >
              {t('resetPassword.success.goToLogin')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/images/autoclipper-logo-full.png"
              alt="Autoclipper Logo"
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-white">{t('resetPassword.title')}</CardTitle>
          <CardDescription className="text-gray-400">
            {t('resetPassword.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">
                {t('resetPassword.newPassword')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t('resetPassword.newPasswordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white">
                {t('resetPassword.confirmNewPassword')}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={t('resetPassword.confirmNewPasswordPlaceholder')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </Button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-lime-400 to-cyan-400 hover:from-lime-500 hover:to-cyan-500 text-black font-semibold rounded-full shadow-lg"
              disabled={isLoading}
            >
              {isLoading ? t('resetPassword.submitting') : t('resetPassword.submitButton')}
            </Button>

            <div className="text-center text-sm text-gray-400">
              <Link
                to={loginDestination}
                className="text-green-400 hover:text-green-300 underline"
              >
                {t('resetPassword.backToLogin')}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
